/**
 * Shared packing-label generator.
 *
 * Renders the physical label format used on the floor:
 *
 *   ┌──────────────────────────────────────────┐
 *   │           BOX NO :- BOX-MJ-01-A           │
 *   ├──────────────────────────────────────────┤
 *   │ Proto Code          : 5096        [ IMG ] │
 *   │ Item Qty            : 2                   │
 *   │ Project Name        : AURIKA SHIMLA       │
 *   │ Project Code        : MJ-26-05            │
 *   │ Product Name        : SOFA                │
 *   │ Product Discription : SOFA LHS - 1 NOS    │
 *   └──────────────────────────────────────────┘
 *
 * The same HTML is used for both the print window and the on-screen preview
 * (via dangerouslySetInnerHTML) so what you see is exactly what prints.
 * Used by both WorkerPackingPage and AdminPackingPage.
 */
import { buildImageUrl } from './imageUrl';

const esc = (v) =>
  String(v == null ? '' : v)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/** Pull the display fields out of a /packing/boxes record. */
export function packingLabelData(box = {}) {
  const items = box.items || [];
  // PART QTY: sum of box contents if present, else the linked item's qty.
  const partQty = items.length
    ? items.reduce((s, it) => s + (Number(it.quantity) || 0), 0)
    : (box.item_quantity != null ? Number(box.item_quantity) : '');

  return {
    projectName: box.project_name || box.client_name || '',
    projectCode: box.proj_code || '',
    productName: box.main_item || box.item_name || (items[0] && items[0].item_name) || '',
    protoCode:   box.photo_code || box.item_proto_code || '',
    description: box.description || box.item_description || box.notes || '',
    partQty,
    boxNo:       box.box_number || '',
    imageUrl:    box.box_image ? buildImageUrl(box.box_image)
               : box.item_image ? buildImageUrl(box.item_image)
               : '',
  };
}

/** CSS shared by print + preview. 100mm x 75mm thermal label. */
export const PACKING_LABEL_CSS = `
  .pl-label { width:100mm; height:75mm; box-sizing:border-box; padding:4mm 5mm;
    border:1.5px solid #000; font-family:Arial, sans-serif; color:#000;
    display:flex; flex-direction:column; gap:1.5mm; overflow:hidden; background:#fff; }
  .pl-boxno { text-align:center; font-weight:800; letter-spacing:0.5px;
    border-bottom:1.5px solid #000; padding-bottom:1.5mm;
    word-break:break-all; overflow-wrap:anywhere;
    font-size:clamp(13px, 4.5vw, 22px); line-height:1.15; }
  .pl-card { flex:1; border:1.5px solid #000; border-radius:8px; padding:2.5mm 3mm;
    display:flex; flex-direction:column; gap:1.5mm; overflow:hidden; }
  .pl-row { flex:1; display:flex; gap:2.5mm; align-items:flex-start; overflow:hidden; }
  .pl-fields { flex:1; min-width:0; display:flex; flex-direction:column; justify-content:center; gap:1.2mm; }
  .pl-desc { border-top:1px solid #000; padding-top:1.2mm; }
  .pl-desc .pl-v { white-space:pre-line; }
  .pl-kv { display:flex; align-items:flex-start; font-size:12px; line-height:1.2; }
  .pl-k { flex:0 0 27mm; font-size:10.5px; font-weight:600; white-space:nowrap; }
  .pl-c { flex:0 0 3mm; }
  .pl-v { flex:1; min-width:0; font-weight:700; word-break:normal; overflow-wrap:break-word; white-space:pre-line; }
  .pl-thumb { flex:0 0 22mm; height:22mm; border:1px solid #999; border-radius:6px;
    overflow:hidden; display:flex; align-items:center; justify-content:center; background:#f7f7f7; }
  .pl-thumb img { width:100%; height:100%; object-fit:cover; }
  @media print {
    @page { size:100mm 75mm; margin:0; }
    html,body { margin:0; padding:0; }
    .pl-boxno { font-size:16px; }
  }
`;

const kv = (label, value) =>
  `<div class="pl-kv"><span class="pl-k">${esc(label)}</span><span class="pl-c">:</span><span class="pl-v">${esc(value)}</span></div>`;

/**
 * Inner label markup (no <html>/<style>) — caller injects PACKING_LABEL_CSS.
 * @param {object} box       box record from /packing/boxes
 * @param {string} displayNum optional override for Box No. (sub-labels A/B/C…)
 */
export function packingLabelInnerHtml(box, displayNum) {
  const d = packingLabelData(box);
  const boxNo = displayNum || d.boxNo;
  const thumb = d.imageUrl
    ? `<div class="pl-thumb"><img src="${esc(d.imageUrl)}" alt="" /></div>`
    : '';

  return `<div class="pl-label">
  <div class="pl-boxno">BOX NO :- ${esc(boxNo)}</div>
  <div class="pl-card">
    <div class="pl-row">
      <div class="pl-fields">
        ${kv('Proto Code', d.protoCode)}
        ${kv('Item Qty', d.partQty)}
        ${kv('Project Name', d.projectName)}
        ${kv('Project Code', d.projectCode)}
        ${kv('Product Name', d.productName)}
      </div>
      ${thumb}
    </div>
    <div class="pl-desc">
      ${kv('Product Discription', d.description)}
    </div>
  </div>
</div>`;
}

/**
 * Returns the array of display-numbers to print for a box.
 *   copies=1  → ['AURIKA-SOFA-01']
 *   copies=4  → ['AURIKA-SOFA-01-A', 'AURIKA-SOFA-01-B', 'AURIKA-SOFA-01-C', 'AURIKA-SOFA-01-D']
 *
 * Uses DASH suffix (not parentheses) so the physical label reads cleanly.
 * Import and call this from every print/export path to keep format consistent.
 */
export function getBoxSubLabels(box) {
  const SUB = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const base = box.box_number || '';
  const n = Math.max(1, Math.min(26, parseInt(box.copies) || 1));
  if (n === 1) return [base];
  return Array.from({ length: n }, (_, i) => base + '-' + SUB[i]);
}

/** Full printable document for one or more labels. */
export function packingLabelPrintDoc(labelsHtml) {
  return `<!DOCTYPE html><html><head><title>Packing Label</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box;}
  body{font-family:Arial,sans-serif;}
  .pl-label{page-break-after:always;}
  .pl-label:last-child{page-break-after:avoid;}
  ${PACKING_LABEL_CSS}
</style></head><body>${labelsHtml}</body></html>`;
}
